import { useEffect, useState } from 'react';

import { OUTBOUND_EMAIL_STATUSES } from '@nexus/shared';

import { ApiError, apiGet } from '../../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CellNote,
  DataTable,
  EmptyState,
  LoadingPage,
  Num,
  PageHeader,
  SelectField,
  Stat,
  StatGrid,
  StatusPill,
  When,
  statusLabel,
  type Column,
} from '../../ui';

interface OutboxRow {
  id: string;
  type: string;
  toEmail: string;
  subject: string;
  status: string;
  attempts: number;
  maxAttempts: number;
  nextAttemptAt: string;
  lastError: string | null;
  sentAt: string | null;
  providerReference: string | null;
  createdAt: string;
  actionUrl: string | null;
  linkPath: string | null;
}

/**
 * The Outbox: every transactional email, and — while no provider is configured
 * — the link itself.
 *
 * THIS PAGE IS WHY THE PILOT CAN RUN WITHOUT AN EMAIL PROVIDER. With
 * `EMAIL_PROVIDER` unset nothing is ever sent; a signup writes a queued row and
 * the operator copies the confirmation link out of here. Once a provider is
 * configured the drain sends the mail and strips the URL from the row, so a
 * delivered row shows where it pointed and never a live token.
 */
export function AdminOutbox(): React.JSX.Element {
  const [rows, setRows] = useState<OutboxRow[] | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('ALL');
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const body = await apiGet<{ provider: string | null; emails: OutboxRow[] }>(
          `/admin/outbox?status=${encodeURIComponent(filter)}`,
        );
        setRows(body.emails);
        setProvider(body.provider);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not load the outbox.');
      }
    })();
  }, [filter]);

  const columns: Array<Column<OutboxRow>> = [
    {
      key: 'to',
      header: 'To',
      primary: true,
      cell: (row) => (
        <>
          {row.toEmail}
          <CellNote>{row.subject}</CellNote>
        </>
      ),
    },
    {
      key: 'type',
      header: 'Kind',
      cell: (row) => <Badge plain>{statusLabel(row.type)}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <>
          <StatusPill status={row.status} domain="email" />
          {row.lastError && <CellNote>{row.lastError}</CellNote>}
        </>
      ),
    },
    {
      key: 'attempts',
      header: 'Attempts',
      numeric: true,
      align: 'end',
      cell: (row) => (
        <span className="num">
          {row.attempts}/{row.maxAttempts}
        </span>
      ),
    },
    {
      key: 'when',
      header: 'Queued',
      cell: (row) => <When value={row.createdAt} withTime />,
    },
    {
      key: 'link',
      header: 'Link',
      cell: (row) =>
        row.actionUrl ? (
          <>
            <code className="chip break">{row.actionUrl}</code>
            <div className="actions">
              <Button
                small
                onClick={() => {
                  void navigator.clipboard?.writeText(row.actionUrl ?? '');
                  setCopied(row.id);
                }}
              >
                {copied === row.id ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </>
        ) : (
          <span className="muted">{row.linkPath ?? '—'}</span>
        ),
    },
  ];

  if (rows === null && !error) return <LoadingPage eyebrow="Operator" title="Outbox" stats />;

  const queued = (rows ?? []).filter((r) => r.status === 'QUEUED').length;
  const failed = (rows ?? []).filter((r) => r.status === 'FAILED').length;

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Outbox"
        lead={
          provider
            ? `Sending through ${provider}. A delivered row keeps the path it pointed at, never the live link.`
            : 'No email provider is configured, so nothing is sent. Copy the link out of the row and give it to the person directly.'
        }
        actions={
          <SelectField
            label="Show"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="ALL">Everything</option>
            {OUTBOUND_EMAIL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {statusLabel(status, 'email')}
              </option>
            ))}
          </SelectField>
        }
      />

      {!provider && (
        <Alert tone="waiting" title="Nothing is actually being sent">
          <span>
            Every row here is a mail that was written and never posted. This is deliberate for the
            pilot: set <code className="chip quiet">EMAIL_PROVIDER</code> to start delivering, and
            the links stop being readable here the moment you do.
          </span>
        </Alert>
      )}

      {error && <Alert tone="critical">{error}</Alert>}

      <StatGrid>
        <Stat label="Rows shown" value={<Num value={(rows ?? []).length} />} />
        <Stat
          label="Queued"
          value={<Num value={queued} />}
          note={provider ? 'Waiting for the drain.' : 'Waiting for you to hand over the link.'}
          emphasis={queued > 0}
        />
        <Stat
          label="Failed"
          value={<Num value={failed} />}
          note={failed > 0 ? 'The provider refused these.' : 'Nothing has bounced.'}
        />
      </StatGrid>

      <Card title="Every email">
        <DataTable
          columns={columns}
          rows={rows ?? []}
          getKey={(row) => row.id}
          empty={
            <EmptyState title="Nothing here.">
              {filter === 'ALL'
                ? 'No mail has been written yet. A signup writes the first row.'
                : 'No mail in that state. Try "Everything".'}
            </EmptyState>
          }
        />
      </Card>
    </>
  );
}
